/**
 * components/shared/ScoreBreakdownCard.tsx
 * The 13-factor score breakdown card — extracted out of AnalysisPanel so the
 * Analytics tab can render the exact same thing for a historical scan
 * record as the Scanner tab renders live, instead of reimplementing it.
 */

import clsx from 'clsx';
import type { ScoreBreakdown } from '../../types';

export function Signal({ label, passed, status, value }: {
  label: string; value: string;
  passed?: boolean; status?: 'pass' | 'fail' | 'skipped';
}) {
  const resolved = status ?? (passed ? 'pass' : 'fail');
  const dotCls  = resolved === 'pass' ? 'bg-bull' : resolved === 'skipped' ? 'bg-ink-3' : 'bg-bear';
  const textCls = resolved === 'pass' ? 'text-bull' : resolved === 'skipped' ? 'text-ink-3' : 'text-bear';
  return (
    <div className="signal-row">
      <div className="flex items-center gap-2">
        <span className={clsx('w-2 h-2 rounded-full flex-shrink-0', dotCls)} />
        <span className="text-xs text-ink-2">{label}</span>
      </div>
      <span className={clsx('text-xs font-mono', textCls)}>
        {value}
      </span>
    </div>
  );
}

export interface OptionSnapshot {
  optionSymbol?: string | null;
  strike?:       number | null;
  expiration?:   string | null;
  bid?:          number | null;
  ask?:          number | null;
  mid?:          number | null;
  spread?:       number | null;
  openInterest?: number | null;
  delta?:        number | null;
  status?:       string | null; // pass | fail | unchecked
}

const OPTION_STATUS_CLS: Record<string, string> = {
  pass:      'text-bull',
  fail:      'text-bear',
  unchecked: 'text-ink-3',
};

export function ScoreBreakdownCard({ score, scoreBreakdown, optionInfo }: {
  score: number;
  scoreBreakdown?: ScoreBreakdown;
  /** The contract that was actually being considered for this scan record —
   * only meaningful for a historical record (the Journal Log's expanded
   * detail); the live Scanner tab already shows a full options chain
   * alongside this card, so it doesn't pass this prop. */
  optionInfo?: OptionSnapshot;
}) {
  const scoreColor = score >= 70 ? 'text-bull' : score >= 50 ? 'text-amber' : 'text-bear';
  const scoreFill   = score >= 70 ? 'bg-bull'   : score >= 50 ? 'bg-amber'   : 'bg-bear';
  const scoreVerdict =
    score >= 70 ? 'HIGH PROBABILITY — Enter full size' :
    score >= 50 ? 'MODERATE — Enter half size' :
                   'LOW PROBABILITY — Skip this trade';

  return (
    <div className="card">
      <div className="flex items-center gap-4 mb-3">
        <div className={clsx('font-display text-4xl font-extrabold leading-none', scoreColor)}>
          {score}
        </div>
        <div className="flex-1">
          <div className="text-sm font-medium mb-1">{scoreVerdict}</div>
          <div className="text-2xs text-ink-3">
            out of 100 · {score >= 70
              ? 'Primary factors aligned — wait for 15-min candle confirmation'
              : score >= 50
              ? 'Some factors missing — reduce size or wait for better setup'
              : 'Too many factors against — skip and wait'}
          </div>
        </div>
      </div>
      <div className="h-2 bg-bg-3 rounded-full overflow-hidden mb-4">
        <div className={clsx('score-bar', scoreFill)} style={{ width: `${score}%` }} />
      </div>

      {scoreBreakdown && (
        <>
          <div className="text-2xs text-ink-3 mb-2">
            Evaluated {scoreBreakdown.evaluatedFactors} of {scoreBreakdown.totalFactors} scoring factors
            {scoreBreakdown.evaluatedFactors < scoreBreakdown.totalFactors &&
              ' — the rest are shown below as "not evaluated," not counted against the score'}
          </div>
          <div className="border-t border-border pt-2">
            {scoreBreakdown.factors.map(f => (
              <Signal
                key={f.name}
                label={f.name}
                status={f.status}
                value={f.status === 'skipped' ? f.value : `${f.points >= 0 ? '+' : ''}${f.points} · ${f.value}`}
              />
            ))}
          </div>
        </>
      )}

      {optionInfo && (optionInfo.optionSymbol || optionInfo.strike != null) && (
        <div className="border-t border-border pt-3 mt-3">
          <div className="text-2xs text-ink-3 uppercase tracking-widest mb-2 flex items-center justify-between">
            <span>Contract Considered</span>
            {optionInfo.status && (
              <span className={clsx('normal-case tracking-normal', OPTION_STATUS_CLS[optionInfo.status] ?? 'text-ink-3')}>
                {optionInfo.status === 'pass' ? 'Liquid ✓' : optionInfo.status === 'fail' ? 'Not liquid ✗' : 'Unchecked'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 text-2xs font-mono">
            <div><span className="text-ink-3">Strike</span><br />
              <span className="text-ink-1">{optionInfo.strike != null ? `$${optionInfo.strike}C` : '—'}</span></div>
            <div><span className="text-ink-3">Expiration</span><br />
              <span className="text-ink-1">{optionInfo.expiration ?? '—'}</span></div>
            <div><span className="text-ink-3">Delta</span><br />
              <span className="text-ink-1">{optionInfo.delta != null ? optionInfo.delta.toFixed(2) : '—'}</span></div>
            <div><span className="text-ink-3">Bid / Ask</span><br />
              <span className="text-ink-1">
                {optionInfo.bid != null ? `$${optionInfo.bid.toFixed(2)}` : '—'} / {optionInfo.ask != null ? `$${optionInfo.ask.toFixed(2)}` : '—'}
              </span></div>
            <div><span className="text-ink-3">Spread</span><br />
              <span className="text-ink-1">{optionInfo.spread != null ? `$${optionInfo.spread.toFixed(2)}` : '—'}</span></div>
            <div><span className="text-ink-3">Open Interest</span><br />
              <span className="text-ink-1">{optionInfo.openInterest != null ? optionInfo.openInterest.toLocaleString() : '—'}</span></div>
          </div>
          {optionInfo.optionSymbol && (
            <div className="mt-2 text-2xs text-ink-3 font-mono">{optionInfo.optionSymbol}</div>
          )}
        </div>
      )}
    </div>
  );
}
